import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import editIcon from "../../../assets/edit-icon.svg";
import eyeIcon from "../../../assets/eye.svg";
import searchIcon from "../../../assets/search-new-icon.svg";
import CLOSE from "../../../assets/CLOSE.svg";
import Switch from "@mui/material/Switch";
import HospitalCustomPaginaion from "../../../utils/HospitalPaginationUtil";
import { ProcedureFlowServices } from "../../../services/ProcedureFlowServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";
import closeIcon from "../../../assets/crossIcon.svg";

const PAGE_SIZE = 10;

export default function ManageSpeciality() {
  const [panelVisible, setPanelVisible] = useState(false);
  const [panelType, setPanelType] = useState("add"); // "add" | "edit" | "view"
  const [currentSpeciality, setCurrentSpeciality] = useState({
    name: "",
    specialityId: "",
  });
  const [specialities, setSpecialities] = useState([]);
  const [specialityLoading, setSpecialityLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [saveLoading, setSaveLoading] = useState(false);
  const [inactiveModalOpen, setInactiveModalOpen] = useState(false);
  const [specialityToInactive, setSpecialityToInactive] = useState(null);
  const [searchKey, setSearchKey] = useState("");
  const [debouncedSearchKey, setDebouncedSearchKey] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [initialSpecialityForEdit, setInitialSpecialityForEdit] = useState(null);
  const [allSpecialities, setAllSpecialities] = useState([]);

  const fetchAllSpecialities = useCallback(async () => {
    try {
      const all = [];
      let pg = 1;
      let hasMore = true;
      while (hasMore) {
        const res = await ProcedureFlowServices.getSpecialityTypes({ page: pg, limit: 500 });
        const list = res?.data?.list ?? [];
        list.forEach((item) => {
          all.push({
            id: item.id,
            name: item.speciality,
            specialityId: item.specialityId,
            status: item.status ?? "ACTIVE",
          });
        });
        const tp = res?.data?.totalPages ?? 1;
        hasMore = pg < tp && list.length === 500;
        pg += 1;
      }
      setAllSpecialities(all);
    } catch (_) {
      setAllSpecialities([]);
    }
  }, []);

  useEffect(() => {
    fetchAllSpecialities();
  }, [fetchAllSpecialities]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchKey(searchKey), 400);
    return () => clearTimeout(timer);
  }, [searchKey]);

  const fetchSpecialityList = useCallback(async () => {
    try {
      setSpecialityLoading(true);
      const response = await ProcedureFlowServices.getSpecialityTypes({
        page,
        limit: PAGE_SIZE,
        ...(debouncedSearchKey.trim() && { searchKey: debouncedSearchKey.trim() }),
      });
      const list = response?.data?.list ?? [];
      setTotalCount(response?.data?.total ?? list.length);
      const mapped = list.map((item) => ({
        id: item.id,
        name: item.speciality,
        specialityId: item.specialityId,
        status: item.status ?? "ACTIVE",
      }));
      setSpecialities(mapped);
      const apiTotalPages = response?.data?.totalPages ?? 1;
      setTotalPages(apiTotalPages > 0 ? apiTotalPages : 1);
    } catch (error) {
      handleApiErrorResponses(error);
      setSpecialities([]);
      setTotalCount(0);
    } finally {
      setSpecialityLoading(false);
    }
  }, [page, debouncedSearchKey]);

  useEffect(() => {
    fetchSpecialityList();
  }, [fetchSpecialityList]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchKey]);

  const handleSave = async () => {
    const name = (currentSpeciality?.name ?? "").trim();
    const specialityId = (currentSpeciality?.specialityId ?? "").trim();
    if (!specialityId) {
      toast.error("Please enter Speciality ID.");
      return;
    }
    if (!name) {
      toast.error("Please enter Speciality Name.");
      return;
    }

    setSaveLoading(true);
    try {
      const payload = [
        {
          specialityId,
          speciality: name,
        },
      ];
      await ProcedureFlowServices.addDefaultSpeciality(payload);
      toast.success(
        panelType === "edit"
          ? "Speciality updated successfully."
          : "Speciality added successfully."
      );
      setPanelVisible(false);
      setCurrentSpeciality({ name: "", specialityId: "" });
      setInitialSpecialityForEdit(null);
      await fetchSpecialityList();
      fetchAllSpecialities();
    } catch (error) {
      handleApiErrorResponses(error);
    } finally {
      setSaveLoading(false);
    }
  };

  const openAdd = () => {
    setPanelVisible(true);
    setPanelType("add");
    setCurrentSpeciality({ name: "", specialityId: "", _alreadyExists: false });
    setInitialSpecialityForEdit(null);
  };

  const openEdit = (speciality) => {
    setPanelVisible(true);
    setPanelType("edit");
    setCurrentSpeciality(speciality);
    setInitialSpecialityForEdit({ ...speciality });
  };

  const openView = (speciality) => {
    setPanelVisible(true);
    setPanelType("view");
    setCurrentSpeciality(speciality);
  };

  const openInactiveModal = (speciality) => {
    setSpecialityToInactive(speciality);
    setInactiveModalOpen(true);
  };

  const closeInactiveModal = () => {
    setInactiveModalOpen(false);
    setSpecialityToInactive(null);
  };

  const handleConfirmInactive = async () => {
    if (!specialityToInactive?.specialityId) {
      closeInactiveModal();
      return;
    }
    try {
      await ProcedureFlowServices.addDefaultSpeciality([
        { specialityId: specialityToInactive.specialityId, status: "INACTIVE" },
      ]);
      toast.success("Speciality inactivated successfully.");
      await fetchSpecialityList();
    } catch (err) {
      handleApiErrorResponses(err);
      toast.error("Failed to inactivate speciality. Please try again.");
    } finally {
      closeInactiveModal();
    }
  };

  return (
    <div className="w-full min-h-screen min-h-[100dvh] flex flex-col">
      {/* Figma: Green gradient background for the entire page */}
      <div className="bg-[radial-gradient(81.98%_338.81%_at_13.41%_276.08%,_rgba(90,198,159,0.5)_0%,_rgba(222,222,222,0.5)_100%)] flex-1 min-h-full w-full pt-6 pb-3 px-3 sm:pt-8 sm:pb-4 sm:px-4 md:pt-12 md:pb-6 md:px-6">
        {/* HEADER: Title, Search, Add Button - on green background */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 sm:gap-6 py-4 sm:py-6 md:py-8 mb-4 sm:mb-6">
          <h1 className="font-lufga font-normal text-[50px] leading-[100%] tracking-[-0.07em] align-middle text-black shrink-0">
            Manage Speciality ({totalCount})
          </h1>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 lg:gap-6 flex-1 lg:flex-initial lg:max-w-[720px]">
            {/* SEARCH: capsule, white, magnifying glass, placeholder Speciality Name */}
            <div className="relative flex-1 min-w-0">
              <img
                src={searchIcon}
                alt="search"
                className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 w-5 h-5 sm:w-6 sm:h-6 pointer-events-none"
              />
              <input
                placeholder="Speciality Name"
                value={searchKey}
                onChange={(e) => setSearchKey(e.target.value)}
                className="w-full h-12 sm:h-14 pl-11 sm:pl-14 pr-5 sm:pr-6 border border-solid border-[#E0E0E0] rounded-[54px] outline-none font-lufga text-sm sm:text-base text-[#1B1B1B] placeholder:text-gray-400 bg-white"
              />
            </div>

            {/* ADD BUTTON: Figma - 237×80, radius 54px, gap 10px, px 24, border 2px, bg #5AC69FCC */}
            <button
              onClick={openAdd}
              className="font-lufga w-[237px] h-[60px] rounded-[54px] gap-[10px] px-6 border-2 border-solid border-[#5AC69F] bg-[#5AC69FCC] flex items-center justify-center font-medium text-black hover:opacity-90 transition-opacity shrink-0"
            >
              <span className="text-xl leading-none">+</span>
              Add New Speciality
            </button>
          </div>
        </div>

        {/* MAIN AREA: White card for table */}
        <div
          className={`grid gap-6 transition-all duration-300 ${
            panelVisible ? "lg:grid-cols-[1fr_minmax(35vw,40vw)]" : "grid-cols-1"
          }`}
        >
          {/* LEFT: TABLE - White card with rounded corners and shadow */}
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm overflow-hidden p-4 sm:p-6">
            {/* Table header: SNo. | Speciality Name (bold, dark grey) */}
            <div className="grid grid-cols-[56px_120px_1fr_120px] gap-4 px-0 sm:px-2 py-3 font-lufga font-semibold text-sm sm:text-base text-[#1B1B1B] border-b border-[#E0E0E0]">
              <span>SNo.</span>
              <span>Speciality ID</span>
              <span>Speciality Name</span>
              <span className="text-right pr-2">Actions</span>
            </div>

            <div className="font-lufga divide-y divide-[#E8E8E8]">
              {specialityLoading ? (
                <div className="py-12 text-center text-gray-500">
                  Loading specialities...
                </div>
              ) : specialities.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  No specialities found.
                </div>
              ) : (
                specialities.map((speciality, index) => (
                  <SpecialityRow
                    key={speciality.id}
                    serialNo={(page - 1) * PAGE_SIZE + index + 1}
                    speciality={speciality}
                    onEdit={() => openEdit(speciality)}
                    onView={() => openView(speciality)}
                    onToggleInactive={() => openInactiveModal(speciality)}
                    onReactivate={async () => {
                      try {
                        await ProcedureFlowServices.addDefaultSpeciality([
                          { specialityId: speciality.specialityId, status: "ACTIVE" },
                        ]);
                        toast.success("Speciality activated successfully.");
                        await fetchSpecialityList();
                      } catch (err) {
                        handleApiErrorResponses(err);
                        toast.error("Failed to activate speciality. Please try again.");
                      }
                    }}
                    alternateBg={index % 2 === 1}
                  />
                ))
              )}
            </div>

            {/* Pagination: centered */}
            {!specialityLoading && specialities.length > 0 && (
              <div className="flex justify-center mt-6 mb-4 sm:mb-6">
                <HospitalCustomPaginaion
                  page={page}
                  setPage={setPage}
                  totalPages={totalPages}
                />
              </div>
            )}
          </div>

          {/* RIGHT PANEL */}
          {panelVisible && (
            <div className="bg-white rounded-2xl sm:rounded-3xl border border-[#E0E0E0] p-6 sm:p-8 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-lufga text-[32px] font-normal">
                  {panelType === "add"
                    ? "Add Speciality"
                    : panelType === "edit"
                    ? "Edit Speciality"
                    : "View Speciality"}
                </h2>
                <img
                  src={CLOSE}
                  alt="close"
                  onClick={() => setPanelVisible(false)}
                  className="w-6 h-6 cursor-pointer"
                />
              </div>

              {/* ADD / EDIT */}
              {(panelType === "add" || panelType === "edit") && (
                <>
                  <div className="relative mb-4">
                    <input
                      type="text"
                      placeholder="Speciality ID (e.g. BM, MC)"
                      value={currentSpeciality?.specialityId ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        const match = allSpecialities.find(
                          (s) =>
                            s.specialityId?.toLowerCase() === val.trim().toLowerCase() &&
                            s.status === "ACTIVE"
                        );
                        if (match) {
                          setCurrentSpeciality({
                            specialityId: val,
                            name: match.name,
                            _alreadyExists: true,
                          });
                        } else {
                          setCurrentSpeciality((prev) => ({
                            ...prev,
                            specialityId: val,
                            _alreadyExists: false,
                            ...(prev._alreadyExists ? { name: "" } : {}),
                          }));
                        }
                      }}
                      disabled={panelType === "edit" || (panelType === "add" && currentSpeciality?._alreadyExists)}
                      className={`w-full h-[70px] px-6 pr-36 border-2 border-solid rounded-[40px] outline-none font-lufga text-[16px] border-[#E0E0E0] ${
                        panelType === "add" && currentSpeciality?._alreadyExists ? "bg-gray-100 text-gray-500" : ""
                      }`}
                    />
                    {(currentSpeciality?.specialityId ?? "").length > 0 && (
                      <span className="absolute right-6 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
                        Speciality ID
                      </span>
                    )}
                  </div>
                  <div className="relative mb-4">
                    <input
                      type="text"
                      placeholder="Name of the Speciality"
                      value={currentSpeciality?.name ?? ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        const match = allSpecialities.find(
                          (s) =>
                            s.name?.toLowerCase() === val.trim().toLowerCase() &&
                            s.status === "ACTIVE"
                        );
                        if (match) {
                          setCurrentSpeciality({
                            specialityId: match.specialityId,
                            name: val,
                            _alreadyExists: true,
                          });
                        } else {
                          setCurrentSpeciality((prev) => ({
                            ...prev,
                            name: val,
                            _alreadyExists: false,
                            ...(prev._alreadyExists ? { specialityId: "" } : {}),
                          }));
                        }
                      }}
                      disabled={panelType === "add" && currentSpeciality?._alreadyExists}
                      className={`w-full h-[70px] px-6 pr-40 border-2 border-solid rounded-[40px] outline-none font-lufga text-[16px] border-[#E0E0E0] ${
                        panelType === "add" && currentSpeciality?._alreadyExists ? "bg-gray-100 text-gray-500" : ""
                      }`}
                    />
                    {(currentSpeciality?.name ?? "").length > 0 && (
                      <span className="absolute right-6 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
                        Speciality Name
                      </span>
                    )}
                  </div>
                  {panelType === "add" && currentSpeciality?._alreadyExists && (
                    <p className="text-sm text-orange-600 font-lufga mb-4 px-2">
                      This speciality already exists in the system.
                    </p>
                  )}
                  {(() => {
                    const isAlreadyExists = panelType === "add" && currentSpeciality?._alreadyExists;
                    const hasChanges =
                      panelType !== "edit" ||
                      !initialSpecialityForEdit ||
                      (currentSpeciality?.name ?? "").trim() !== (initialSpecialityForEdit?.name ?? "").trim() ||
                      (currentSpeciality?.specialityId ?? "").trim() !== (initialSpecialityForEdit?.specialityId ?? "").trim();
                    return (
                      <button
                        onClick={
                          isAlreadyExists
                            ? () =>
                                setCurrentSpeciality({ name: "", specialityId: "", _alreadyExists: false })
                            : handleSave
                        }
                        disabled={!isAlreadyExists && (saveLoading || !hasChanges)}
                        className={`w-full h-[70px] border-2 border-solid rounded-[40px] font-lufga font-medium transition disabled:opacity-60 disabled:cursor-not-allowed ${
                          isAlreadyExists
                            ? "border-orange-600 bg-orange-500 text-black hover:bg-orange-600"
                            : "border-[#5AC69F] bg-emerald-400 hover:bg-emerald-500"
                        }`}
                      >
                        {saveLoading && !isAlreadyExists
                          ? "Saving..."
                          : panelType === "edit"
                          ? "Save Changes"
                          : isAlreadyExists
                          ? "Reset"
                          : "Add Speciality"}
                      </button>
                    );
                  })()}
                </>
              )}

              {/* VIEW */}
              {panelType === "view" && (
                <div className="font-lufga text-gray-700 space-y-3">
                  <div>
                    <strong>Speciality ID:</strong>{" "}
                    {currentSpeciality?.specialityId ?? "-"}
                  </div>
                  <div>
                    <strong>Speciality Name:</strong> {currentSpeciality?.name}
                  </div>
                  <div>
                    <strong>Created On:</strong> 2026-01-23
                  </div>
                  <div>
                    <strong>Created By:</strong> Admin
                  </div>
                  <div>
                    <strong>Upload On:</strong> 2026-01-23
                  </div>
                  <div>
                    <strong>Upload By:</strong> Admin
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {inactiveModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-sm px-4">
            <div className="relative w-full max-w-md bg-white rounded-3xl shadow-xl px-8 py-8">
              <button
                onClick={closeInactiveModal}
                className="absolute top-6 right-6 bg-transparent border-none p-0 cursor-pointer"
              >
                <img src={closeIcon} alt="close" className="w-5 h-5" />
              </button>
              <p className="text-center font-lufga font-medium text-gray-800 text-lg mt-2 pr-8">
                Do you want to inactive Speciality ?
              </p>
              <div className="flex justify-center mt-8">
                <button
                  onClick={handleConfirmInactive}
                  className="rounded-[40px] px-8 py-4 min-h-[56px] border-2 border-solid border-[#5AC69F] bg-[#5AC69F] hover:opacity-90 font-lufga font-semibold text-white transition-opacity"
                >
                  Inactive
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ROW: SNo. | Speciality Name (wrapping, dark grey) | eye | edit | toggle (green when active) */
function SpecialityRow({ serialNo, speciality, onEdit, onView, onToggleInactive, onReactivate, alternateBg }) {
  const isActive = speciality?.status === "ACTIVE";
  const [enabled, setEnabled] = useState(isActive);

  useEffect(() => {
    setEnabled(isActive);
  }, [isActive]);

  const handleToggle = () => {
    if (enabled) {
      onToggleInactive?.();
    } else {
      onReactivate?.();
      setEnabled(true);
    }
  };

  return (
    <div
      className={`grid grid-cols-[56px_120px_1fr_120px] gap-4 px-0 sm:px-2 py-4 sm:py-5 items-center font-lufga ${
        alternateBg ? "bg-[#E8E8E833]" : ""
      }`}
    >
      <span className="font-medium text-sm sm:text-base text-[#1B1B1B]">
        {serialNo}
      </span>

      <span className="font-normal text-sm sm:text-base text-[#1B1B1B]">
        {speciality.specialityId}
      </span>

      <p className="text-sm sm:text-base text-[#1B1B1B] text-gray-800 leading-relaxed break-words min-w-0">
        {speciality.name}
      </p>

      <div className="flex items-center gap-3 sm:gap-4 justify-end pr-2">
        <img
          src={eyeIcon}
          alt="view"
          className="w-5 h-5 cursor-pointer flex-shrink-0"
          onClick={onView}
        />
        <img
          src={editIcon}
          alt="edit"
          className="w-5 h-5 cursor-pointer flex-shrink-0"
          onClick={onEdit}
        />
        <Switch
          checked={enabled}
          onChange={handleToggle}
          size="small"
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: "#5AC69F",
              "& + .MuiSwitch-track": { backgroundColor: "#5AC69F !important", opacity: 0.6 },
            },
            "& .MuiSwitch-track": {
              backgroundColor: "#9e9e9e !important",
            },
          }}
        />
      </div>
    </div>
  );
}
